import { useMutation } from "@tanstack/react-query";
import { t } from "i18next";
import { toast } from "sonner";

import OfferAPI from "../api/OfferAPI";
import {
  planFormToApi,
  planFormToCreateApi,
} from "../components/offers/offerPlanAdapter";

import type { UpdateOfferPlanResponse } from "../api/OfferAPI.types";
import type { FlatOfferPlan } from "../components/offers/offerPlanAdapter";

export interface UseOfferPlanMutationsParams {
  projectSlug: string;
  offerId: string;
}

export interface UseOfferPlanMutationsResult {
  savePlan: (plan: FlatOfferPlan) => Promise<UpdateOfferPlanResponse>;
  isSaving: boolean;
}

export function useOfferPlanMutations({
  projectSlug,
  offerId,
}: UseOfferPlanMutationsParams): UseOfferPlanMutationsResult {
  const mutation = useMutation<UpdateOfferPlanResponse, unknown, FlatOfferPlan>(
    {
      mutationFn: async (plan: FlatOfferPlan) => {
        if (plan.isNew) {
          return OfferAPI.createOfferPlan({
            projectSlug,
            plan: planFormToCreateApi(plan, offerId),
          });
        }
        return OfferAPI.updateOfferPlan({
          projectSlug,
          plan: planFormToApi(plan),
        });
      },
      onSuccess: () => {
        toast.success(t("editor-offers:savePlanSuccess"));
      },
      onError: () => {
        toast.error(t("editor-offers:savePlanError"), { duration: 3000 });
      },
    },
  );

  return {
    savePlan: async (plan: FlatOfferPlan) => mutation.mutateAsync(plan),
    isSaving: mutation.isLoading,
  };
}
